import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiSave, FiPlus, FiEdit3, FiTrash2 } from "react-icons/fi";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import { toast, ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

const ModuleTable = () => {
  const [modules, setModules] = useState([]);
  const [newModules, setNewModules] = useState([{ module_name: "" }]);

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = useMemo(() => (key, def = "") => translations[key] || def, [translations]);

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/modules`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      setModules(response.data);
    } catch (error) {
      toast.error("Modüller alınamadı!");
    }
  };

  const handleInputChange = (index, value) => {
    const updated = [...newModules];
    updated[index].module_name = value;
    setNewModules(updated);
  };

  const addNewRow = () => {
    setNewModules([...newModules, { module_name: "" }]);
  };

  const saveModules = async () => {
    const validRows = newModules.filter((mod) => mod.module_name.trim());
    if (!validRows.length) {
      toast.warn("Geçerli modül başlığı giriniz.");
      return;
    }
    try {
      for (const row of validRows) {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/modules`, row, {
          headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        });
      }
      toast.success("Modül başarıyla eklendi!");
      setNewModules([{ module_name: "" }]);
      fetchModules();
    } catch (error) {
      toast.error("Modül kaydedilemedi!");
    }
  };

  const handleToggleStatus = async (id, currentStatus, rowIndex) => {
    const newStatus = !currentStatus;
    setModules((prev) =>
      prev.map((mod, i) => (i === rowIndex ? { ...mod, status: newStatus } : mod))
    );
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/modules/status/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }
      );
    } catch (error) {
      toast.error("Durum güncellenemedi!");
      // rollback
      setModules((prev) =>
        prev.map((mod, i) => (i === rowIndex ? { ...mod, status: currentStatus } : mod))
      );
    }
  };

  const handleDelete = async (id) => {
    confirmAlert({
      title: "Modül Silinsin mi?",
      message: "Modülü Silmek Üzeresiniz",
      buttons: [
        {
          label: "Evet, Sil",
          onClick: async () => {
            try {
              await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/modules/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
              });
              toast.success("Modül başarıyla silindi!");
              fetchModules();
            } catch (error) {
              toast.error(" Modül silinemedi!");
            }
          },
        },
        {
          label: "İptal",
          onClick: () => {
            toast.info("Silme işlemi iptal edildi.");
          },
        },
      ],
    });
  };
  

  const columns = useMemo(() => [
    {
      accessorKey: "id",
      header: getText("TABLE_ID_LABEL"),
      meta: { headerClassName: "text-start", className: "text-start" },
    },
    {
      accessorKey: "module_name",
      header: getText("MODULE_NAME_LABEL"),
      meta: { headerClassName: "text-start", className: "text-start" },
    },
    {
      accessorKey: "status",
      header: getText("TABLE_STATUS_LABEL"),
      meta: { headerClassName: "text-end", className: "text-end" },
      cell: ({ row }) => {
        const rowIndex = row.index;
        const moduleId = row.original.id;
        const isChecked = row.original.status;

        const toggleStatus = () =>
          handleToggleStatus(moduleId, isChecked, rowIndex);

        return (
          <div className="form-check form-switch text-end d-flex align-items-end justify-content-end">
            <input
              className="form-check-input custom-checkbox"
              type="checkbox"
              role="switch"
              checked={isChecked}
              onChange={toggleStatus}
              style={{
                cursor: "pointer",
                width: "45px",
                height: "22px",
                transition: "0.3s ease-in-out",
              }}
            />
            <span
              className={`badge ms-2 ${isChecked ? "badge-success" : "badge-danger"}`}
              style={{
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                transition: "0.3s ease-in-out",
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "actions",
      header: () => <div style={{ textAlign: "right" }}>{getText("TABLE_ACTIONS_LABEL")}</div>,
      enableSorting: false,
      meta: { headerClassName: "text-end", className: "text-end" },
      cell: ({ row }) => (
        <div className="hstack gap-2 justify-content-end">
          
          <button
            className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
            title={getText("DELETE")}
            onClick={() => handleDelete(row.original.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ], [getText]);

  return (
    <div className="container mt-4">
      <ToastContainer />
      <Table data={modules} columns={columns} />

      <div className="card card-body general-info mt-4">
        <h5 className="fw-bold mb-3">{getText("ADD_NEW_MODULE")}</h5>
        {newModules.map((mod, index) => (
          <div key={index} className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder={getText("MODULE_NAME_LABEL")}
              value={mod.module_name}
              onChange={(e) => handleInputChange(index, e.target.value)}
            />
          </div>
        ))}

        <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={addNewRow}
            >
              <FiPlus /> {getText("ADD_NEW_ROW")}
            </button>
          </div>
        
        
          <div className="d-flex justify-content-center">
            <button className="btn btn-primary" onClick={saveModules}>
              <FiSave /> {getText("SAVE_BTN")}
            </button>
          </div>
      </div>
    </div>
  );
};

export default ModuleTable;
